import { useEffect, useRef } from 'react';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { HeatmapLayer } from '@deck.gl/aggregation-layers';
import { ScatterplotLayer } from '@deck.gl/layers';

function canCreateWebGL() {
    try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
        if (!gl) return false;
        gl.getExtension('WEBGL_lose_context')?.loseContext();
        return true;
    } catch {
        return false;
    }
}

const LeadDestinationHeatOverlay = ({ map, visible, destinations = [], onSelect }) => {
    const overlayRef = useRef(null);

    useEffect(() => {
        if (!map) return undefined;
        if (!canCreateWebGL()) {
            console.warn('Lead heat overlay skipped: WebGL is disabled in this browser.');
            return undefined;
        }
        // Non-interleaved keeps pointerEvents:none on the deck canvas, so the
        // basemap keeps handling pan/marker clicks on this raster map.
        let overlay;
        try {
            overlay = new GoogleMapsOverlay({
                interleaved: false,
                onError: (error) => {
                    console.warn('Lead heat overlay stopped:', error?.message || error);
                },
            });
            overlay.setMap(map);
        } catch (error) {
            console.warn('Lead heat overlay skipped:', error?.message || error);
            return undefined;
        }
        overlayRef.current = overlay;
        return () => {
            overlay.setProps({ layers: [] });
            overlay.finalize();
            overlayRef.current = null;
        };
    }, [map]);

    useEffect(() => {
        const overlay = overlayRef.current;
        if (!overlay) return;
        if (!visible || !destinations.length) {
            overlay.setProps({ layers: [] });
            return;
        }

        overlay.setProps({
            layers: [
                new HeatmapLayer({
                    id: 'lead-destination-heat',
                    data: destinations,
                    getPosition: (d) => [d.lng, d.lat],
                    getWeight: (d) => d.lead_count || 1,
                    radiusPixels: 48,
                    intensity: 1,
                    threshold: 0.03,
                    colorRange: [
                        [255, 237, 160],
                        [254, 196, 79],
                        [253, 141, 60],
                        [240, 59, 32],
                        [189, 0, 38],
                    ],
                }),
                new ScatterplotLayer({
                    id: 'lead-destination-hits',
                    data: destinations,
                    pickable: true,
                    opacity: 0.35,
                    stroked: true,
                    filled: true,
                    radiusUnits: 'pixels',
                    getPosition: (d) => [d.lng, d.lat],
                    getRadius: (d) => Math.min(18, 6 + (d.lead_count || 1)),
                    getFillColor: [240, 59, 32, 45],
                    getLineColor: [189, 0, 38, 170],
                    lineWidthMinPixels: 1,
                    onClick: (info) => {
                        if (info?.object) onSelect?.(info.object);
                    },
                }),
            ],
        });
    }, [visible, destinations, onSelect]);

    return null;
};

export default LeadDestinationHeatOverlay;
